import { createClient } from '@/lib/supabase';

/**
 * Subscription plan details interface
 */
export interface SubscriptionPlan {
  id: string;
  name: string;
  tier: 'sirloin' | 'ribeye' | 'tomahawk';
  description: string;
  priceMonthly: number;
  maxRequestsPerMonth: number;
  maxTeamMembers: number;
  prioritySupport: boolean;
  features: string[];
}

/**
 * Subscription status interface
 */
export interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  hasInactiveSubscription: boolean;
  planType?: string;
}

/**
 * Active subscription interface
 */
export interface ActiveSubscription {
  id: string;
  planId: string;
  userId: string;
  status: string;
  autoRenew: boolean;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  requestsUsedThisPeriod: number;
  plan?: SubscriptionPlan;
  // Additional fields for display
  planName?: string;
  amount?: number;
  billingCycle?: string;
  nextBillingDate?: string | null;
}

/**
 * Billing transaction interface
 */
export interface BillingTransaction {
  id: string;
  userId: string;
  subscriptionId?: string;
  amount: number;
  currency: string;
  status: 'pending' | 'paid' | 'failed' | 'refunded' | 'cancelled';
  description: string;
  invoiceNumber: string;
  invoiceUrl?: string;
  transactionDate?: string;
  date?: string; // Alternative field name
  paidDate?: string;
  dueDate?: string;
  paymentMethodLast4?: string;
}

/**
 * Payment method interface
 */
export interface PaymentMethod {
  id: string;
  userId: string;
  methodType: 'credit_card' | 'debit_card' | 'bank_account' | 'paypal';
  cardBrand?: string;
  lastFour: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
  billingEmail?: string;
}

/**
 * Get user's subscription status
 */
export async function getSubscriptionStatus(userId: string): Promise<SubscriptionStatus> {
  const supabase = createClient();
  
  try {
    const { data: subscription, error } = await supabase
      .from('client_subscriptions')
      .select('status, plan_type')
      .eq('user_id', userId)
      .single();

    if (error || !subscription) {
      return {
        hasActiveSubscription: false,
        hasInactiveSubscription: false,
      };
    }

    return {
      hasActiveSubscription: subscription.status === 'active',
      hasInactiveSubscription: subscription.status !== 'active',
      planType: subscription.plan_type,
    };
  } catch (error) {
    console.error('Error fetching subscription status:', error);
    return {
      hasActiveSubscription: false,
      hasInactiveSubscription: false,
    };
  }
}

/**
 * Convert snake_case to camelCase
 */
function toCamelCase<T extends Record<string, any>>(obj: T): any {
  const result: any = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    result[camelKey] = obj[key];
  }
  return result;
}

/**
 * Convert camelCase to snake_case
 */
function toSnakeCase<T extends Record<string, any>>(obj: T): any {
  const result: any = {};
  for (const key in obj) {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    result[snakeKey] = obj[key];
  }
  return result;
}

export const subscriptionService = {
  /**
   * Get all available subscription plans
   */
  async getPlans(): Promise<SubscriptionPlan[]> {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .order('price_monthly', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch subscription plans: ${error.message}`);
    }

    return data?.map(toCamelCase) || [];
  },

  /**
   * Get user's active subscription with plan details
   */
  async getActiveSubscription(userId: string): Promise<ActiveSubscription | null> {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from('client_subscriptions').select(`*,plan:subscription_plans(*)`).eq('user_id', userId).eq('status', 'active')
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No active subscription
      throw new Error(`Failed to fetch subscription: ${error.message}`);
    }

    if (!data) return null;

    // Map the database fields to the expected interface
    const subscription: ActiveSubscription = {
      id: data.id,
      planId: data.plan_id,
      userId: data.user_id,
      status: data.status,
      autoRenew: data.auto_renew,
      currentPeriodStart: data.current_period_start,
      currentPeriodEnd: data.current_period_end,
      requestsUsedThisPeriod: data.requests_used_this_period,
      plan: data.plan ? toCamelCase(data.plan) : undefined,
      // Additional fields for display
      planName: data.plan?.name || 'Unknown Plan',
      amount: data.plan?.price_monthly ? Math.round(data.plan.price_monthly * 100) : 0, // Convert dollars to cents
      billingCycle: 'month',
      nextBillingDate: data.current_period_end
    };

    return subscription;
  },

  /**
   * Create new subscription via Supabase Edge Function
   */
  async createSubscription(planId: string, paymentMethodId?: string) {
    const supabase = createClient();
    
    const { data, error } = await supabase.functions.invoke('create-subscription', {
      body: {
        planId,
        paymentMethodId
      }
    });

    if (error) {
      throw new Error(error.message || 'Failed to create subscription');
    }

    return data;
  },

  /**
   * Update subscription (change plan, update auto-renew)
   */
  async updateSubscription(subscriptionId: string, updates: {
    planId?: string;
    autoRenew?: boolean;
  }) {
    const supabase = createClient();
    
    const { data, error } = await supabase.functions.invoke('manage-subscription', {
      body: {
        action: 'update',
        subscriptionId,
        ...updates
      }
    });

    if (error) {
      throw new Error(error.message || 'Failed to update subscription');
    }

    return data;
  },

  /**
   * Cancel subscription
   */
  async cancelSubscription(subscriptionId: string, reason?: string) {
    const supabase = createClient();
    
    const { data, error } = await supabase.functions.invoke('manage-subscription', {
      body: {
        action: 'cancel',
        subscriptionId,
        cancellationReason: reason
      }
    });

    if (error) {
      throw new Error(error.message || 'Failed to cancel subscription');
    }

    return data;
  },

  /**
   * Get billing history for user
   */
  async getBillingHistory(userId: string): Promise<BillingTransaction[]> {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from('billing_transactions').select('*').eq('user_id', userId).order('transaction_date', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch billing history: ${error.message}`);
    }

    return data?.map(toCamelCase) || [];
  },

  /**
   * Get user's payment methods
   */
  async getPaymentMethods(userId: string): Promise<PaymentMethod[]> {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from('payment_methods')
      .select('*')
      .eq('user_id', userId)
      .order('is_default', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch payment methods: ${error.message}`);
    }

    return data?.map(toCamelCase) || [];
  },

  /**
   * Add payment method via Stripe
   */
  async addPaymentMethod(paymentMethodId: string, setAsDefault: boolean = false) {
    const supabase = createClient();
    
    const { data, error } = await supabase.functions.invoke('manage-subscription', {
      body: {
        action: 'add_payment_method',
        paymentMethodId,
        setAsDefault
      }
    });

    if (error) {
      throw new Error(error.message || 'Failed to add payment method');
    }

    return data;
  },

  /**
   * Remove payment method
   */
  async removePaymentMethod(paymentMethodId: string) {
    const supabase = createClient();
    
    const { data, error } = await supabase.functions.invoke('manage-subscription', {
      body: {
        action: 'remove_payment_method',
        paymentMethodId
      }
    });

    if (error) {
      throw new Error(error.message || 'Failed to remove payment method');
    }

    return data;
  },

  /**
   * Set payment method as default
   */
  async setDefaultPaymentMethod(paymentMethodId: string) {
    const supabase = createClient();
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('payment_methods')
      .update({ is_default: false })
      .eq('user_id', user.id);

    if (error) {
      throw new Error(`Failed to update payment methods: ${error.message}`);
    }

    const { error: setError } = await supabase
      .from('payment_methods')
      .update({ is_default: true })
      .eq('id', paymentMethodId);

    if (setError) {
      throw new Error(`Failed to set default payment method: ${setError.message}`);
    }

    return { success: true };
  }
};